import { getErrorMessage } from '@/api/errors'
import { Button } from '@/components/ui/button'
import { extractValidationMessage } from '@/lib/validationErrors'
import { formatFileSize, validateAttachmentFiles } from '@/lib/attachments'
import {
  useSkillAttachments,
  useUploadSkillAttachments,
  useDeleteSkillAttachment,
  useSkillAttachmentDownloadUrl,
} from '@/hooks'
import { Download, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { SkillAttachmentRow, SkillAttachmentsLayout } from './SkillAttachmentsLayout'

interface SkillAttachmentsPanelProps {
  projectId: string
  skillId: string
  canEdit: boolean
}

export function SkillAttachmentsPanel({ projectId, skillId, canEdit }: SkillAttachmentsPanelProps) {
  const { data: attachments = [], isLoading, isError, refetch } = useSkillAttachments(projectId, skillId)
  const upload = useUploadSkillAttachments(projectId, skillId)
  const removeAttachment = useDeleteSkillAttachment(projectId, skillId)
  const download = useSkillAttachmentDownloadUrl(projectId, skillId)

  const onFilesSelected = async (files: File[]) => {
    const { valid: toUpload, errors } = validateAttachmentFiles(files, attachments.length)
    errors.forEach(message => toast.error(message))

    if (toUpload.length === 0) return
    try {
      await upload.mutateAsync(toUpload)
      toast.success(toUpload.length === 1 ? 'Attachment uploaded' : 'Attachments uploaded')
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to upload attachment')
    }
  }

  const onDelete = async (attachmentId: string) => {
    try {
      await removeAttachment.mutateAsync(attachmentId)
      toast.success('Attachment removed')
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to remove attachment')
    }
  }

  const onDownload = (attachmentId: string) => {
    download.mutate(attachmentId, {
      onError: (error) => toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to start download'),
    })
  }

  if (isLoading) {
    return <div className="py-6 text-sm text-muted-foreground">Loading attachments…</div>
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-destructive" role="alert" data-testid="skill-attachments-error">
        <span>Couldn't load attachments.</span>
        <button className="underline" onClick={() => refetch()}>Retry</button>
      </div>
    )
  }

  return (
    <SkillAttachmentsLayout
      canEdit={canEdit}
      isEmpty={attachments.length === 0}
      isUploading={upload.isPending}
      onFilesSelected={files => void onFilesSelected(files)}
    >
      {attachments.map(attachment => (
        <SkillAttachmentRow
          key={attachment.id}
          fileName={attachment.fileName}
          details={`${attachment.contentType} · ${formatFileSize(attachment.sizeBytes)}`}
          testId={`skill-attachment-${attachment.id}`}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDownload(attachment.id)}
            disabled={download.isPending && download.variables === attachment.id}
            aria-label={`Download ${attachment.fileName}`}
          >
            <Download className="h-4 w-4" />
            Download
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              onClick={() => onDelete(attachment.id)}
              disabled={removeAttachment.isPending && removeAttachment.variables === attachment.id}
              aria-label={`Remove ${attachment.fileName}`}
            >
              <Trash2 className="h-4 w-4" />
              Remove
            </Button>
          )}
        </SkillAttachmentRow>
      ))}
    </SkillAttachmentsLayout>
  )
}
